import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Banknote, ChevronRight } from "lucide-react";

const METHODS = [
  { id: "paypal", label: "PayPal", placeholder: "PayPal email address" },
  { id: "bank", label: "Bank Transfer", placeholder: "IBAN / account number + routing" },
  { id: "crypto", label: "Crypto (USDC)", placeholder: "Wallet address (ERC-20)" },
];

const STATUS_STYLES = {
  pending: "text-amber-300 border-amber-500/40",
  approved: "text-sky-300 border-sky-500/40",
  paid: "text-emerald-400 border-emerald-500/40",
  rejected: "text-red-400 border-red-500/40",
};

export default function CashoutSection() {
  const { refresh, user } = useAuth();
  const [limits, setLimits] = useState(null);
  const [rows, setRows] = useState([]);
  const [amount, setAmount] = useState(1000);
  const [method, setMethod] = useState("paypal");
  const [destination, setDestination] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [l, w] = await Promise.all([
        api.get("/withdrawals/limits"),
        api.get("/withdrawals"),
      ]);
      setLimits(l.data);
      setRows(w.data.withdrawals);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/withdrawals", {
        amount_chips: Number(amount),
        method,
        destination: destination.trim(),
        notes: notes.trim() || null,
      });
      toast.success("Cashout request submitted");
      setDestination(""); setNotes("");
      await refresh();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cashout failed");
    } finally { setBusy(false); }
  };

  const usd = (chips) => (chips / (limits?.chips_per_usd || 100)).toFixed(2);
  const maxAllowed = Math.min(limits?.remaining_chips || 0, limits?.user_chips || user?.chips || 0);
  const canSubmit = amount >= (limits?.min_chips || 1000) && amount <= maxAllowed && destination.trim().length >= 3;

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <div className="pill pill-gold inline-flex items-center gap-1"><Banknote className="w-3.5 h-3.5" /> Cashout</div>
          <h2 className="font-display text-2xl sm:text-3xl mt-2">Withdraw your winnings</h2>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-4">
        <form onSubmit={submit} className="vexis-card p-5 sm:p-6 space-y-4" data-testid="cashout-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Amount (chips)</label>
              <input type="number" className="vexis mt-1" min={limits?.min_chips || 1000} max={maxAllowed}
                value={amount} onChange={(e) => setAmount(Math.max(0, parseInt(e.target.value || "0")))}
                data-testid="cashout-amount" />
              <div className="text-xs text-slate-400 font-mono mt-1">≈ ${usd(amount)} USD</div>
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Payout method</label>
              <div className="mt-1 flex flex-wrap gap-2">
                {METHODS.map((m) => (
                  <button type="button" key={m.id} onClick={() => setMethod(m.id)} data-testid={`cashout-method-${m.id}`}
                    className={`px-3 py-1.5 rounded-full text-sm border transition ${method === m.id ? "border-[color:var(--gold)] text-[color:var(--gold)] bg-[rgba(226,185,106,0.08)]" : "border-white/10 text-slate-400 hover:text-white"}`}>
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Destination</label>
            <input className="vexis mt-1" placeholder={METHODS.find(m => m.id === method).placeholder}
              value={destination} onChange={(e) => setDestination(e.target.value)} data-testid="cashout-destination" />
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Notes (optional)</label>
            <textarea className="vexis mt-1" rows={2} placeholder="Anything the payout team should know"
              value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="cashout-notes" />
          </div>

          <button type="submit" className="btn-gold w-full inline-flex items-center justify-center gap-2" disabled={!canSubmit || busy} data-testid="cashout-submit">
            {busy ? "Submitting…" : `Request cashout · $${usd(amount)}`} <ChevronRight className="w-4 h-4" />
          </button>

          <div className="rounded-xl border border-amber-500/25 bg-[rgba(251,191,36,0.06)] px-4 py-3 text-xs text-amber-200 space-y-1" data-testid="cashout-timing-notice">
            <div className="font-mono uppercase tracking-widest text-[10px] text-amber-300">Processing time</div>
            <p className="text-amber-100/90 leading-relaxed">
              Cashout requests are reviewed manually and typically settle within <span className="font-semibold">3–5 business days</span>. Chips are held on submission and released to your destination once payout clears — you&apos;ll see a reference in your history when it&apos;s paid.
            </p>
          </div>
        </form>

        <div className="vexis-card p-5" data-testid="cashout-limits">
          <div className="pill">Your limits</div>
          <ul className="mt-3 text-sm space-y-2">
            <li className="flex justify-between"><span className="text-slate-400">Chip → USD</span><span className="font-mono">{limits?.chips_per_usd || 100} chips = $1</span></li>
            <li className="flex justify-between"><span className="text-slate-400">Minimum</span><span className="font-mono">{limits?.min_chips || 1000} chips</span></li>
            <li className="flex justify-between"><span className="text-slate-400">Deposited</span><span className="font-mono">${limits?.deposited_usd?.toFixed(2) || "0.00"}</span></li>
            <li className="flex justify-between"><span className="text-slate-400">Cap ({limits?.cap_multiplier || 10}× deposit)</span><span className="font-mono">{(limits?.max_chips_lifetime || 0).toLocaleString()} chips</span></li>
            <li className="flex justify-between"><span className="text-slate-400">Daily cap</span><span className="font-mono">${limits?.daily_cap_usd?.toFixed(0) || 500}</span></li>
            <li className="flex justify-between"><span className="text-slate-400">Used today</span><span className="font-mono">${limits?.daily_used_usd?.toFixed(2) || "0.00"}</span></li>
            <li className="flex justify-between border-t border-white/10 pt-2 mt-2">
              <span className="text-slate-300">Available now</span>
              <span className="font-mono text-[color:var(--gold)]" data-testid="cashout-remaining">{maxAllowed.toLocaleString()} chips</span>
            </li>
          </ul>
          {(!limits || limits.deposited_usd === 0) && (
            <p className="text-xs text-amber-300 mt-3">Buy at least one chip pack to unlock cashouts (cap is 10× your total deposits).</p>
          )}
          {limits?.paypal_live === false && (
            <p className="text-xs text-slate-500 mt-3">PayPal is in simulated mode — add PAYPAL_CLIENT_ID / PAYPAL_CLIENT_SECRET to activate real payouts.</p>
          )}
        </div>
      </div>

      <div className="vexis-card overflow-hidden">
        <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
          <div className="pill">Your requests</div>
          <button onClick={load} className="text-xs text-slate-400 hover:text-white" data-testid="cashout-refresh">Refresh</button>
        </div>
        <ul className="divide-y divide-white/5">
          {rows.map((w) => (
            <li key={w._id} className="px-5 py-3 grid grid-cols-12 items-center text-sm gap-2" data-testid={`cashout-row-${w._id}`}>
              <div className="col-span-4 sm:col-span-3">
                <div className="font-mono">${w.amount_usd?.toFixed(2)}</div>
                <div className="text-xs text-slate-500">{w.amount_chips.toLocaleString()} chips</div>
              </div>
              <div className="col-span-3 sm:col-span-2 text-slate-300 capitalize">{w.method}</div>
              <div className="hidden sm:block sm:col-span-4 text-xs text-slate-500 font-mono truncate" title={w.payout_reference || w.destination}>
                {w.payout_reference ? <><span className="text-slate-400">ref </span>{w.payout_reference}</> : w.destination}
              </div>
              <div className="col-span-5 sm:col-span-3 text-right">
                <span className={`pill ${STATUS_STYLES[w.status] || ""}`}>{w.status === "paid" ? "auto-paid" : w.status}</span>
              </div>
            </li>
          ))}
          {rows.length === 0 && <li className="px-5 py-6 text-slate-500 text-sm">No cashout requests yet.</li>}
        </ul>
      </div>
    </div>
  );
}
